import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldAlert, Upload, UsersRound } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { MultiSelectFilter } from '@/client/components/admin/MultiSelectFilter';
import { useSegmentDetailQuery, useSegmentOptionsQuery } from '@/client/api/campaigns/lifecycle.query';
import {
  useCreateSegmentMutation,
  usePreviewSegmentMutation,
  useUpdateSegmentMutation,
  useUploadSegmentCsvMutation,
} from '@/client/api/campaigns/lifecycle.mutation';
import {
  OptionCount,
  SegmentFilters,
  SegmentPreviewResult,
  SegmentSource,
  SegmentSourceParams,
  segmentId,
} from '@/client/types/campaigns';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import SegmentMembersPanel from './SegmentMembersPanel';

const SOURCES: { id: SegmentSource; label: string; hint: string }[] = [
  { id: 'users', label: 'Platform users', hint: 'People with a FeelyFeely account, narrowed by the filters below.' },
  { id: 'waitlist', label: 'Waitlist', hint: 'People who signed up before launch (Users > Waitlist).' },
  {
    id: 'prospects',
    label: 'Sales prospects',
    hint: 'Only prospects who said yes to email and are not marked do-not-contact. No answer counts as no.',
  },
  {
    id: 'upload',
    label: 'Uploaded list',
    hint: 'A CSV where every row has an email or a phone number. Every other column becomes a placeholder.',
  },
];

// What each source can fill in campaign copy, beyond uploaded columns.
const BASE_FIELDS: Record<SegmentSource, string[]> = {
  users: ['first_name', 'last_name', 'name', 'email', 'phone'],
  waitlist: ['first_name', 'last_name', 'name', 'email', 'phone', 'interest', 'campaign_type'],
  prospects: ['first_name', 'last_name', 'name', 'email'],
  upload: [],
};

const GENDER_LABEL: Record<string, string> = { M: 'Male', F: 'Female', O: 'Other' };

let regionNames: Intl.DisplayNames | null = null;
try {
  regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch {
  regionNames = null;
}
const countryName = (code: string) => {
  try {
    return regionNames?.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
};

/** Picker options with the number of people behind each value. */
function opts<K extends string | number>(list: OptionCount<K>[] | undefined, label?: (k: K) => string) {
  return (list ?? []).map((o) => ({
    key: o.key,
    label: `${o.label ?? (label ? label(o.key) : String(o.key))} (${o.count.toLocaleString()})`,
  }));
}

const asList = (v?: string | string[]) => (Array.isArray(v) ? v : v ? [v] : []);
const toEpoch = (d: string) => (d ? Math.floor(new Date(`${d}T00:00:00`).getTime() / 1000) : null);
const fromEpoch = (n?: number | null) => (n ? new Date(n * 1000).toISOString().slice(0, 10) : '');

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Requires <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">campaign.segment</code>.
    </p>
  </div>
);

const TriBool = ({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  disabled?: boolean;
}) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
    <select
      disabled={disabled}
      value={value === true ? '1' : value === false ? '0' : ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '1' ? true : v === '0' ? false : null);
      }}
      className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
    >
      <option value="">Any</option>
      <option value="1">Yes</option>
      <option value="0">No</option>
    </select>
  </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <fieldset className="space-y-3">
    <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</legend>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </fieldset>
);

const input = 'h-9 w-full rounded-md border border-gray-300 px-3 text-sm';

const SegmentEditorPage = () => {
  const { sid } = useParams<{ sid?: string }>();
  const isEdit = Boolean(sid);
  const navigate = useNavigate();

  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/campaigns' },
    { label: 'Segments', href: '/admin/relationship/segments' },
    { label: isEdit ? 'Edit' : 'New' },
  ]);

  const segmentPerm = useHasPermission('campaign.segment');
  const viewPerm = useHasPermission('campaign.view');
  const canWrite = segmentPerm.isSuperAdmin || segmentPerm.hasPermission;
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission || canWrite;
  const permLoading = segmentPerm.isLoading;

  const detailQ = useSegmentDetailQuery(sid ?? null, isEdit && canView && !permLoading);
  const optionsQ = useSegmentOptionsQuery(canView && !permLoading);
  const o = optionsQ.data;
  const create = useCreateSegmentMutation();
  const update = useUpdateSegmentMutation();
  const preview = usePreviewSegmentMutation();
  const upload = useUploadSegmentCsvMutation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState<SegmentSource>('users');
  // Platform-user filters
  const [countries, setCountries] = useState<string[]>(['GH']);
  const [locales, setLocales] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [orgIds, setOrgIds] = useState<number[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [active, setActive] = useState<boolean | null>(true);
  const [tester, setTester] = useState<boolean | null>(false);
  const [adminHold, setAdminHold] = useState<boolean | null>(false);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [phoneVerified, setPhoneVerified] = useState<boolean | null>(null);
  const [hasListing, setHasListing] = useState<boolean | null>(null);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [lastActiveDays, setLastActiveDays] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  // Waitlist / prospects
  const [waitlistTypes, setWaitlistTypes] = useState<string[]>([]);
  const [waitlistInterests, setWaitlistInterests] = useState<string[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [kinds, setKinds] = useState<string[]>([]);
  // Upload
  const [csvRef, setCsvRef] = useState<string | null>(null);
  const [csvName, setCsvName] = useState('');
  const [csvCount, setCsvCount] = useState<number | null>(null);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [csvSkipped, setCsvSkipped] = useState<{ count: number; lines: number[] } | null>(null);

  const [previewResult, setPreviewResult] = useState<SegmentPreviewResult | null>(null);
  const [hydrated, setHydrated] = useState(!isEdit);

  useEffect(() => {
    if (!isEdit || !detailQ.data || hydrated) return;
    const s = detailQ.data;
    const f = s.filters || {};
    const sp = s.source_params || {};
    setName(s.name || '');
    setDescription(s.description || '');
    setSource(s.source || 'users');
    setCountries(f.country_iso2 || []);
    setLocales(f.locale || []);
    setGenders(f.gender || []);
    setRoles(f.roles || []);
    setOrgIds(f.org_ids || []);
    setTagIds(f.tags || []);
    setActive(f.active ?? null);
    setTester(f.tester ?? null);
    setAdminHold(f.admin_hold ?? null);
    setEmailVerified(f.email_verified ?? null);
    setPhoneVerified(f.phone_verified ?? null);
    setHasListing(f.has_listing ?? null);
    setCreatedAfter(fromEpoch(f.created_after));
    setCreatedBefore(fromEpoch(f.created_before));
    setLastActiveDays(f.last_active_days != null ? String(f.last_active_days) : '');
    setAgeMin(f.age_min != null ? String(f.age_min) : '');
    setAgeMax(f.age_max != null ? String(f.age_max) : '');
    setWaitlistTypes(asList(sp.campaign_type));
    setWaitlistInterests(asList(sp.interest));
    setStages(sp.stages || []);
    setKinds(asList(sp.kind));
    setCsvRef(sp.csv_ref || null);
    setCsvName(sp.csv_name || '');
    setCsvCount(sp.csv_count ?? null);
    setCsvColumns(sp.csv_columns || []);
    setHydrated(true);
  }, [detailQ.data, hydrated, isEdit]);

  const buildFilters = (): SegmentFilters => {
    const after = toEpoch(createdAfter);
    // "Signed up before" includes that whole day.
    const before = createdBefore ? (toEpoch(createdBefore) as number) + 86400 : null;
    return {
      ...(countries.length ? { country_iso2: countries } : {}),
      ...(locales.length ? { locale: locales } : {}),
      ...(genders.length ? { gender: genders } : {}),
      ...(roles.length ? { roles } : {}),
      ...(orgIds.length ? { org_ids: orgIds } : {}),
      ...(tagIds.length ? { tags: tagIds } : {}),
      ...(active !== null ? { active } : {}),
      ...(tester !== null ? { tester } : {}),
      ...(adminHold !== null ? { admin_hold: adminHold } : {}),
      ...(emailVerified !== null ? { email_verified: emailVerified } : {}),
      ...(phoneVerified !== null ? { phone_verified: phoneVerified } : {}),
      ...(hasListing !== null ? { has_listing: hasListing } : {}),
      ...(after ? { created_after: after } : {}),
      ...(before ? { created_before: before } : {}),
      ...(lastActiveDays ? { last_active_days: Number(lastActiveDays) } : {}),
      ...(ageMin ? { age_min: Number(ageMin) } : {}),
      ...(ageMax ? { age_max: Number(ageMax) } : {}),
    };
  };

  const buildSourceParams = (): SegmentSourceParams => {
    if (source === 'waitlist') {
      return {
        ...(waitlistTypes.length ? { campaign_type: waitlistTypes } : {}),
        ...(waitlistInterests.length ? { interest: waitlistInterests } : {}),
      };
    }
    if (source === 'prospects') {
      return { ...(stages.length ? { stages } : {}), ...(kinds.length ? { kind: kinds } : {}) };
    }
    if (source === 'upload' && csvRef) {
      return {
        csv_ref: csvRef,
        csv_name: csvName,
        csv_columns: csvColumns,
        ...(csvCount != null ? { csv_count: csvCount } : {}),
      };
    }
    return {};
  };

  // Placeholders campaigns can fill from this segment.
  const fillable = useMemo(
    () => [...new Set([...BASE_FIELDS[source], ...(source === 'upload' ? csvColumns : [])])].filter((f) => f !== 'uid'),
    [source, csvColumns],
  );

  const handlePreview = async () => {
    if (source === 'upload' && !csvRef) {
      toast.error('Upload a CSV first', { richColors: true });
      return;
    }
    try {
      const result = await preview.mutateAsync({
        source,
        source_params: buildSourceParams(),
        filters: buildFilters(),
        idempotencyKey: generateIdempotencyKey(),
      });
      setPreviewResult(result);
    } catch (error: any) {
      toast.error(error?.message || 'Preview failed', { richColors: true });
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name is required', { richColors: true });
      return;
    }
    if (source === 'upload' && !csvRef) {
      toast.error('Upload a CSV first', { richColors: true });
      return;
    }
    const filters = source === 'users' ? buildFilters() : {};
    const sourceFields = { source, source_params: buildSourceParams() };
    try {
      if (isEdit && sid) {
        const updated = await update.mutateAsync({
          sid,
          body: { name: name.trim(), description: description.trim() || null, ...sourceFields, filters },
          idempotencyKey: generateIdempotencyKey(),
        });
        toast.success('Segment updated', { richColors: true });
        navigate(`/admin/relationship/segments/${segmentId(updated) || sid}`);
      } else {
        const created = await create.mutateAsync({
          body: {
            name: name.trim(),
            ...(description.trim() ? { description: description.trim() } : {}),
            ...sourceFields,
            filters,
          },
          idempotencyKey: generateIdempotencyKey(),
        });
        toast.success('Segment created', { richColors: true });
        navigate(`/admin/relationship/segments/${segmentId(created)}`);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Save failed', { richColors: true });
    }
  };

  const handleCsv = async (file: File | null) => {
    if (!file) return;
    try {
      const result = await upload.mutateAsync(file);
      const rows = result.count ?? result.total ?? null;
      setCsvRef(result.csv_ref || null);
      setCsvName(file.name);
      setCsvCount(rows);
      setCsvColumns(result.columns || []);
      setCsvSkipped(result.skipped ? { count: result.skipped, lines: result.skipped_lines || [] } : null);
      setPreviewResult(null);
      toast.success(`${file.name}: ${rows ?? '?'} usable rows`, { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Upload failed', { richColors: true });
    }
  };

  if (permLoading || (isEdit && !hydrated && detailQ.isLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!canWrite && !(isEdit && canView)) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  const saving = create.isPending || update.isPending;
  const readOnly = isEdit && !canWrite;
  const optionsLoading = optionsQ.isLoading;
  const pick = 'w-full';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <Link
            to="/admin/relationship/segments"
            className="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Segments
          </Link>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <UsersRound className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">{isEdit ? 'Edit segment' : 'New segment'}</h1>
          </div>
        </div>

        {isEdit && sid && <SegmentMembersPanel sid={sid} />}

        <div className="space-y-6 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Name</label>
              <input
                disabled={readOnly}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={input}
                placeholder="GH sellers with listings"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Description</label>
              <input
                disabled={readOnly}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={input}
                placeholder="Optional"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Who is in this segment</label>
            <div className="grid gap-2 sm:grid-cols-4">
              {SOURCES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  disabled={readOnly}
                  onClick={() => {
                    setSource(s.id);
                    setPreviewResult(null);
                  }}
                  className={`rounded-md border px-3 py-2 text-left text-sm ${
                    source === s.id
                      ? 'border-blue-300 bg-blue-50 font-medium text-blue-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-gray-500">{SOURCES.find((s) => s.id === source)?.hint}</p>
          </div>

          {optionsLoading && source !== 'upload' && (
            <p className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading filter values…
            </p>
          )}

          {source === 'users' && (
            <div className="space-y-6">
              <Section title="Where and language">
                <MultiSelectFilter
                  label="Countries"
                  searchable
                  capitalize={false}
                  triggerClassName={pick}
                  options={opts(o?.users.countries, countryName)}
                  value={countries}
                  onChange={setCountries}
                />
                <MultiSelectFilter
                  label="Locales"
                  searchable
                  capitalize={false}
                  triggerClassName={pick}
                  options={opts(o?.users.locales)}
                  value={locales}
                  onChange={setLocales}
                />
                <MultiSelectFilter
                  label="Gender"
                  capitalize={false}
                  triggerClassName={pick}
                  options={opts(o?.users.genders, (k) => GENDER_LABEL[k] || k)}
                  value={genders}
                  onChange={setGenders}
                />
              </Section>

              <Section title="Roles and membership">
                <MultiSelectFilter
                  label="Roles"
                  searchable
                  triggerClassName={pick}
                  options={opts(o?.users.roles, (k) => k.replace(/_/g, ' '))}
                  value={roles}
                  onChange={setRoles}
                />
                <MultiSelectFilter
                  label="Organisation members"
                  searchable
                  capitalize={false}
                  triggerClassName={pick}
                  options={opts(o?.users.orgs)}
                  value={orgIds}
                  onChange={setOrgIds}
                />
                <MultiSelectFilter
                  label="Admin tags"
                  searchable
                  capitalize={false}
                  anyLabel={o && o.users.tags.length === 0 ? 'No tags in use' : 'Any'}
                  triggerClassName={pick}
                  options={opts(o?.users.tags)}
                  value={tagIds}
                  onChange={setTagIds}
                />
              </Section>

              <Section title="Account">
                <TriBool label="Active" value={active} onChange={setActive} disabled={readOnly} />
                <TriBool label="Tester" value={tester} onChange={setTester} disabled={readOnly} />
                <TriBool label="On admin hold" value={adminHold} onChange={setAdminHold} disabled={readOnly} />
                <TriBool label="Email verified" value={emailVerified} onChange={setEmailVerified} disabled={readOnly} />
                <TriBool label="Phone verified" value={phoneVerified} onChange={setPhoneVerified} disabled={readOnly} />
                <TriBool label="Has a listing" value={hasListing} onChange={setHasListing} disabled={readOnly} />
              </Section>

              <Section title="Activity and age">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Signed up from</label>
                  <input
                    type="date"
                    disabled={readOnly}
                    value={createdAfter}
                    onChange={(e) => setCreatedAfter(e.target.value)}
                    className={input}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Signed up until</label>
                  <input
                    type="date"
                    disabled={readOnly}
                    value={createdBefore}
                    onChange={(e) => setCreatedBefore(e.target.value)}
                    className={input}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Active in the last (days)</label>
                  <input
                    type="number"
                    min={1}
                    disabled={readOnly}
                    value={lastActiveDays}
                    onChange={(e) => setLastActiveDays(e.target.value)}
                    className={input}
                    placeholder="Any time"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Age from</label>
                  <input
                    type="number"
                    min={0}
                    disabled={readOnly}
                    value={ageMin}
                    onChange={(e) => setAgeMin(e.target.value)}
                    className={input}
                    placeholder="Any"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Age to</label>
                  <input
                    type="number"
                    min={0}
                    disabled={readOnly}
                    value={ageMax}
                    onChange={(e) => setAgeMax(e.target.value)}
                    className={input}
                    placeholder="Any"
                  />
                </div>
              </Section>
            </div>
          )}

          {source === 'waitlist' && (
            <Section title="Narrow the waitlist">
              <MultiSelectFilter
                label="Where they came from"
                searchable
                triggerClassName={pick}
                options={opts(o?.waitlist.campaign_type)}
                value={waitlistTypes}
                onChange={setWaitlistTypes}
              />
              <MultiSelectFilter
                label="Interest"
                searchable
                capitalize={false}
                triggerClassName={pick}
                options={opts(o?.waitlist.interest)}
                value={waitlistInterests}
                onChange={setWaitlistInterests}
              />
            </Section>
          )}

          {source === 'prospects' && (
            <Section title="Narrow the prospects">
              <MultiSelectFilter
                label="Stage"
                searchable
                triggerClassName={pick}
                options={opts(o?.prospects.stages)}
                value={stages}
                onChange={setStages}
              />
              <MultiSelectFilter
                label="Type"
                triggerClassName={pick}
                options={opts(o?.prospects.kinds)}
                value={kinds}
                onChange={setKinds}
              />
            </Section>
          )}

          {source === 'upload' && (
            <div className="rounded-md border border-dashed border-gray-300 p-4">
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                <Upload className="h-4 w-4" />
                {csvRef ? 'Replace the list' : 'Upload a CSV'}
              </label>
              {!readOnly && (
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => handleCsv(e.target.files?.[0] ?? null)}
                  className="text-sm"
                />
              )}
              <p className="mt-2 text-xs text-gray-500">
                Every row needs an <span className="font-mono">email</span> or a <span className="font-mono">phone</span>{' '}
                (a FeelyFeely <span className="font-mono">uid</span> also works). Use{' '}
                <span className="font-mono">first_name</span>, <span className="font-mono">last_name</span> or{' '}
                <span className="font-mono">name</span> for greetings. Any other column, such as{' '}
                <span className="font-mono">company</span>, becomes a placeholder like{' '}
                <span className="font-mono">{'{{company}}'}</span>.
              </p>
              {upload.isPending && (
                <p className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
                </p>
              )}
              {csvRef && (
                <p className="mt-3 text-sm text-gray-800">
                  Current list: {csvName || 'uploaded CSV'}
                  {csvCount != null && ` (${csvCount.toLocaleString()} rows)`}
                </p>
              )}
              {csvSkipped && (
                <p className="mt-1 text-xs text-amber-700">
                  Skipped {csvSkipped.count.toLocaleString()} rows with no email or phone
                  {csvSkipped.lines.length > 0 &&
                    ` (line${csvSkipped.lines.length > 1 ? 's' : ''} ${csvSkipped.lines.join(', ')}${
                      csvSkipped.count > csvSkipped.lines.length ? ', …' : ''
                    })`}
                  .
                </p>
              )}
            </div>
          )}

          {fillable.length > 0 && (
            <div className="rounded-md bg-gray-50 px-4 py-3">
              <p className="mb-1.5 text-xs font-medium text-gray-500">Placeholders this segment can fill</p>
              <div className="flex flex-wrap gap-1.5">
                {fillable.map((f) => (
                  <code key={f} className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-700 ring-1 ring-gray-200">
                    {`{{${f}}}`}
                  </code>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                Not everyone has every value. The campaign page shows who is missing what before you start, and{' '}
                <span className="font-mono">{'{{field|fallback}}'}</span> covers the gaps.
              </p>
            </div>
          )}

          {previewResult && (
            <div className="rounded-md border border-gray-100 p-4 text-sm">
              <p className="font-medium text-gray-900">
                {previewResult.estimated_size?.toLocaleString() ?? '—'} people
              </p>
              {(previewResult.sample?.length ?? 0) > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-gray-600">
                  {previewResult.sample!.slice(0, 8).map((u, i) => (
                    <li key={u.uid ?? u.email ?? i}>
                      {[u.name, u.uid ? `uid ${u.uid}` : null, u.email || u.phone].filter(Boolean).join(' · ')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" disabled={preview.isPending} onClick={handlePreview} className="gap-2">
              {preview.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Preview
            </Button>
            {!readOnly && (
              <Button disabled={saving} onClick={handleSave} className="gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? 'Save' : 'Create'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentEditorPage;
